import { Link } from 'react-router-dom'

import { formatEffectivePeriod } from '@/features/devices/lib/format-effective-period'
import type { DeviceHistory } from '@/features/devices/types/device-history'
import type { Pagination } from '@/lib/api/types'
import { LoadingSpinner, PaginationLinks } from '@/shared/components'
import type { Location } from '@/shared/types/location'

type DeviceHistoryListProps = {
  histories: DeviceHistory[]
  pagination: Pagination
  isLoading: boolean
  error: string | null
  onPageChange: (page: number) => void
}

const LocationSummary = ({ location }: { location: Location }) => {
  const locationDetails = [location.town, location.asset_location].filter(Boolean)

  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-bold text-foreground">{location.location_id}</span>
      {locationDetails.length > 0 ? (
        <span className="text-sm text-muted-foreground">
          {locationDetails.join(', ')}
        </span>
      ) : null}
    </div>
  )
}

export const DeviceHistoryList = ({
  histories,
  pagination,
  isLoading,
  error,
  onPageChange,
}: DeviceHistoryListProps) => {
  if (isLoading) {
    return <LoadingSpinner position="center" label="Loading device history" />
  }

  if (error) {
    return (
      <p className="p-4 text-sm text-destructive" role="alert">
        {error}
      </p>
    )
  }

  if (histories.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        No history for this device.
      </p>
    )
  }

  return (
    <div className="flex flex-col items-center gap-6 p-6">
      {histories.map((history) => (
        <article
          key={history.id}
          className="flex flex-col w-1/2 gap-3 rounded-lg border border-outline/40 bg-background p-4"
        >
          <div className="flex flex-row justify-between">
            <LocationSummary location={history.location} />

            {history.job ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <Link
                  to={`/jobs/${history.job.id}`}
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                >
                  View job
                </Link>
              </div>
            ) : null}
          </div>

          <p className="text-sm text-foreground">
            {formatEffectivePeriod(
              history.effective_period.started_at,
              history.effective_period.ended_at
            )}
          </p>
        </article>
      ))}

      {pagination.total_pages > 1 ? (
        <PaginationLinks
          pagination={pagination}
          onPageChange={onPageChange}
          className="pt-1"
        />
      ) : null}
    </div>
  )
}
